import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import {
  ConvertableTypes,
  CreditNote,
} from '@work4all/components/lib/components/convert-popover/ConvertableTypes';
import { DropTarget } from '@work4all/components/lib/dataDisplay/card-widget/hooks/use-widget-drag-drop-context';

import { useDataProvider, usePermissions, useUser } from '@work4all/data';

import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { Offer } from '@work4all/models/lib/Classes/Offer.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileType } from '@work4all/models/lib/File';

import { typeNameToEntity } from '@work4all/utils';
import { canAddSalesOpportunities } from '@work4all/utils/lib/permissions';

import { useCanFilterEntitiesByPermission } from '../../../../../../../components/data-tables/offers-table/hooks/use-can-filter-entities-by-permission';
import {
  CloneConvertDialogCallbacks,
  useCloneConvertDialogCallbacks,
} from '../../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-dialog';
import { ENTITY_BY_CONVERT_CONFIGS } from '../../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-erp';
import { useConvertBzObjectMutation } from '../../../../../../../components/data-tables/offers-table/hooks/use-convert-bz-object-mutation';
import { useConvertBzObjectToSalesOpportunity } from '../../../../../../../components/data-tables/offers-table/hooks/use-convert-bz-object-to-sales-opportunity-mutation';
import {
  ErpDialogConfirm,
  OnConfirmOptions,
} from '../../../../../../../components/data-tables/offers-table/types';
import { useEntityHandlers } from '../../../../../../../components/data-tables/use-entity-handlers';
import { useCanCloneDeliveryNote } from '../../../../../../../hooks';
import { usePermissionsRights } from '../../../../../../../hooks/use-permissions-rights';
import { useFileOpenMask } from '../../../../../../mask-overlays/mask-overlay/hooks/use-open-mask';
import { FileContext } from '../../../FileContext';

import {
  ENTITIES_TO_CHECK_CONVERT_PERMISSION_CRM,
  useEntityRequestData,
} from './use-entity-request-data';

export const useCardDnd = (
  targetEntityProp: Entities,
  convertibleEntities: Entities[] = []
) => {
  const fileContext = useContext(FileContext);
  const { t } = useTranslation();
  const dialogs = useDialogs();

  const user = useUser();
  const { mutate } = useConvertBzObjectMutation({
    targetEntity: targetEntityProp,
  });
  const { item: monitorItem } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
  }));

  const sourceEntity = useMemo(() => {
    return typeNameToEntity(monitorItem?.__typename);
  }, [monitorItem]);

  const { requestData, skip } = useEntityRequestData(monitorItem, sourceEntity);

  const response = useDataProvider(requestData, !sourceEntity || skip);

  const [item, setItem] = useState(monitorItem);
  useEffect(() => {
    if (response.data?.length) {
      const firstItem = response.data?.[0] ?? {};
      setItem({ ...monitorItem, ...firstItem });
    } else if (skip) {
      setItem({ ...monitorItem });
    }
  }, [monitorItem, response.data, skip]);

  const onConfirm = useCallback(
    async (confirm: ErpDialogConfirm[], options: OnConfirmOptions) => {
      const promises = confirm.map((singleConfirm) =>
        mutate({
          sourceEntity,
          sourceIds: [item.id],
          targetEntity: options.targetEntity,
          ...singleConfirm,
        })
      );
      const response = await Promise.all(promises);

      const key =
        sourceEntity === Entities.contract &&
        targetEntityProp === Entities.order
          ? 'COMMON.ERP.CONVERT.SUCCESS_CONTRACT'
          : 'COMMON.ERP.CONVERT.SUCCESS';
      if (response.flatMap((x) => x.errors).length === 0)
        await dialogs.alert({
          title: t('COMMON.INFO'),
          description: confirm.length > 1 ? t(key + '_MULTI') : t(key),
          closeLabel: t('COMMON.OK'),
        });
      else {
        await dialogs.alert({
          title: t('MASK.WARNING'),
          description: t('ERROR.COULD_NOT_REACH_SERVICE'),
          closeLabel: t('ALERTS.CLOSE'),
        });
      }
    },
    [sourceEntity, targetEntityProp, dialogs, t, mutate, item?.id]
  );

  const dialogProps: Omit<CloneConvertDialogCallbacks, 'open'> = useMemo(() => {
    return {
      onConfirm,
      sourceEntity,
      targetEntity: targetEntityProp,
      data: [item],
    };
  }, [item, sourceEntity, onConfirm, targetEntityProp]);

  const { open } = useCloneConvertDialogCallbacks(dialogProps);

  const mutateToSales = useConvertBzObjectToSalesOpportunity();

  const { isErpLightUser } = usePermissionsRights();

  const { canConvert } = usePermissions();
  const canUserConvertRecord = canConvert({ entity: targetEntityProp });
  const { filterEntitiesByPermission } = useCanFilterEntitiesByPermission();

  const canCloneDeliveryNote = useCanCloneDeliveryNote();

  const onOpenMask = useFileOpenMask();
  const entityHandlers = useEntityHandlers({
    entity: sourceEntity,
    onOpenMask,
    entries: useMemo(() => (item ? [item] : []), [item]),
  });

  const value = useMemo(() => {
    const canDrag = () => {
      // Full context: https://work4all.atlassian.net/browse/WW-7195
      if (canCloneDeliveryNote && targetEntityProp === Entities.deliveryNote) {
        return true;
      }

      const canUserConvertRecord =
        convertibleEntities.includes(targetEntityProp);

      /**
       * If the user has the right to convert & the entity was defined
       * inside `ENTITY_BY_CONVERT_CONFIGS` as a key, that means you
       * can convert from it to other entities so you can drag an item
       * from the entity list.
       */
      return (
        canUserConvertRecord && !!ENTITY_BY_CONVERT_CONFIGS[targetEntityProp]
      );
    };
    const canDrop = (targetDropType?: DropTarget) => {
      // Full context: https://work4all.atlassian.net/browse/WW-7195
      if (
        canCloneDeliveryNote &&
        sourceEntity === Entities.deliveryNote &&
        targetEntityProp === Entities.deliveryNote
      ) {
        return true;
      }

      // Full context: https://work4all.atlassian.net/browse/WW-6286
      if (isErpLightUser)
        return (
          sourceEntity === Entities.contract &&
          targetEntityProp === Entities.deliveryNote
        );

      if (targetDropType === CreditNote && item) {
        const isCreditNote =
          item.value < 0 || item.positionList?.some((pos) => pos.amount < 0);
        // Only allow credit note conversion if invoice is in RA status
        const isInvoiceInRa = (item as Invoice)?.ra === true;
        return !isCreditNote && isInvoiceInRa;
      }
      if (targetEntityProp === sourceEntity) return canUserConvertRecord;

      const sourceEntityConfigs = ENTITY_BY_CONVERT_CONFIGS[sourceEntity];
      if (
        targetEntityProp === Entities.salesOpportunities &&
        !canAddSalesOpportunities(user)
      )
        return false;
      return (
        sourceEntityConfigs &&
        sourceEntityConfigs.allowedEntities
          .filter(filterEntitiesByPermission)
          .includes(targetEntityProp)
      );
    };
    const canSkip = () => {
      // Full context: https://work4all.atlassian.net/browse/WW-7195
      if (
        canCloneDeliveryNote &&
        targetEntityProp === Entities.deliveryNote &&
        sourceEntity === Entities.deliveryNote
      ) {
        return false;
      }

      if (targetEntityProp === sourceEntity) return false;

      const sourceEntityConfigs = ENTITY_BY_CONVERT_CONFIGS[sourceEntity];
      const allowedEntities = sourceEntityConfigs?.allowedEntities ?? [];
      const blockedEntities = sourceEntityConfigs?.blockedEntities ?? [];

      return ![...allowedEntities, ...blockedEntities].includes(
        targetEntityProp
      );
    };

    const isEntitiesIdentical = sourceEntity === targetEntityProp;

    const onDrop = async (
      item: object,
      targetEntity?: Entities | CreditNote
    ) => {
      if (!('id' in item && typeof item.id === 'number'))
        throw new Error('Wrong dropped item in useCardDnd');

      if (targetEntity === CreditNote) {
        open({
          targetEntity: Entities.invoice,
          targetEntityOptions: {
            convertableTarget: CreditNote,
            invoiceAsCreditNote: true,
          },
        });
        return;
      }
      if (ENTITIES_TO_CHECK_CONVERT_PERMISSION_CRM.includes(sourceEntity)) {
        entityHandlers.convert(targetEntityProp);
      } else if (
        (targetEntity ?? targetEntityProp) === Entities.salesOpportunities
      ) {
        mutateToSales(sourceEntity, [item] as Offer[]);
      } else {
        open(
          targetEntity
            ? {
                targetEntity,
              }
            : undefined
        );
      }
    };

    return {
      canDrag,
      canDrop,
      canSkip,
      onDrop,
      isEntitiesIdentical,
      customerToSupplierConversionEntity:
        calculateCustomerToSupplierConversionEntity(
          sourceEntity,
          targetEntityProp,
          fileContext?.type
        ),
    };
  }, [
    sourceEntity,
    targetEntityProp,
    fileContext?.type,
    canCloneDeliveryNote,
    convertibleEntities,
    isErpLightUser,
    item,
    canUserConvertRecord,
    user,
    filterEntitiesByPermission,
    mutateToSales,
    open,
    entityHandlers,
  ]);

  return value;
};

const calculateCustomerToSupplierConversionEntity = (
  sourceEntity: Entities,
  targetEntity: Entities,
  fileType: FileType
): ConvertableTypes => {
  // Order confirmation to Order conversion
  if (
    sourceEntity === Entities.contract &&
    targetEntity === Entities.contract &&
    fileType !== FileType.PROJECT
  ) {
    return Entities.order;
  }
  if (sourceEntity === Entities.invoice && targetEntity === Entities.invoice) {
    return CreditNote;
  }

  return null;
};
